import { useRef, useState } from "react";
import { Upload, X, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtError, mediaUrl } from "../../lib/api";
import { useContent } from "../../context/SiteContext";
import { Switch } from "../ui/switch";

const base = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

export const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd);
  return data;
};

export const Uploader = ({ accept, multiple, onDone, testid, label = "Upload" }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = [...e.target.files];
    if (!files.length) return;
    setBusy(true);
    try {
      const res = [];
      for (const f of files) res.push(await uploadFile(f));
      await onDone(res);
      toast.success(res.length > 1 ? `${res.length} files uploaded` : "Uploaded");
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept={accept} multiple={multiple} className="hidden" onChange={pick} data-testid={`${testid}-file`} />
      <button type="button" onClick={() => ref.current.click()} disabled={busy} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium hover:bg-slate-50" data-testid={`${testid}-upload-btn`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {busy ? "Uploading..." : label}
      </button>
    </>
  );
};

const RefsInput = ({ field, value, onChange, tid }) => {
  const { items } = useContent(field.resource);
  const list = value || [];
  const toggle = (id) => onChange(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  if (!items) return <p className="text-sm text-slate-400">Loading...</p>;
  if (!items.length) return <p className="text-sm text-slate-400">Nothing published yet.</p>;
  return (
    <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid={tid}>
      {items.map((it) => (
        <label key={it.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
          <input type="checkbox" checked={list.includes(it.id)} onChange={() => toggle(it.id)} data-testid={`${tid}-${it.id}`} />
          <span className="flex-1 truncate">{it[field.labelKey || "title"]}</span>
          {field.subKey && <span className="text-xs text-slate-400">{it[field.subKey]}</span>}
        </label>
      ))}
    </div>
  );
};

export const FieldInput = ({ field, value, onChange, form, setForm }) => {
  const tid = `field-${field.name}`;
  if (field.type === "refs") return <RefsInput field={field} value={value} onChange={onChange} tid={tid} />;
  if (field.type === "switch") return (
    <span className="flex items-center gap-3">
      <Switch checked={value !== false} onCheckedChange={onChange} data-testid={tid} />
      <span className={`text-sm font-medium ${value !== false ? "text-emerald-700" : "text-slate-500"}`}>{value !== false ? field.on || "Yes" : field.off || "No"}</span>
    </span>
  );
  if (field.type === "combo") return (
    <>
      <input list={`${tid}-list`} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} data-testid={tid} />
      <datalist id={`${tid}-list`}>{field.options.map((o) => <option key={o} value={o} />)}</datalist>
    </>
  );
  if (field.type === "textarea") return <textarea rows={field.rows || 4} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} data-testid={tid} />;
  if (field.type === "select") return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} data-testid={tid}>
      {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  if (field.type === "image" || field.type === "file") return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.type === "image" ? "Upload or paste image URL" : "Upload or paste file URL"} className={base} data-testid={tid} />
        <Uploader accept={field.accept || (field.type === "image" ? "image/*" : ".pdf,.doc,.docx")} testid={tid} onDone={([r]) => {
          if (field.fileNameField) setForm({ ...form, [field.name]: r.url, [field.fileNameField]: r.file_name });
          else if (field.thumbField) setForm({ ...form, [field.name]: r.url, [field.thumbField]: r.thumb || "" });
          else onChange(r.url);
        }} />
      </div>
      {field.hint && <p className="text-xs text-slate-500">{field.hint}</p>}
      {value && field.type === "image" && (
        <div className="relative inline-block">
          <img src={mediaUrl(value)} alt="" className={field.round ? "h-28 w-28 rounded-full object-cover object-top" : "h-24 rounded-lg object-cover"} data-testid={`${tid}-preview`} />
          <button type="button" onClick={() => onChange("")} className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-600 text-white grid place-items-center" data-testid={`${tid}-clear`} aria-label="Remove image"><X className="h-3 w-3" /></button>
        </div>
      )}
      {value && field.type === "file" && <a href={mediaUrl(value)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-700"><FileText className="h-4 w-4" />Open file</a>}
    </div>
  );
  if (field.type === "images") {
    const list = value || [];
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          {list.map((u, i) => (
            <div key={i} className="relative">
              <img src={mediaUrl(u)} alt="" className="h-20 w-20 rounded-lg object-cover" />
              <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-600 text-white grid place-items-center" data-testid={`${tid}-remove-${i}`}><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
        <Uploader accept="image/*" multiple testid={tid} onDone={(res) => onChange([...list, ...res.map((r) => r.url)])} />
      </div>
    );
  }
  return <input type={field.type || "text"} value={value ?? ""} onChange={(e) => onChange(field.type === "number" ? Number(e.target.value) : e.target.value)} className={base} data-testid={tid} />;
};
